<template>
    <v-card elevation="7">
        <v-card-text class="pa-0">
            <v-container fluid>
                <v-row>
                    <v-col class="py-0">
                        <scope-options-table
                            title="Roles"
                            :headers="roleHeaders"
                            :items="assignedRoles"
                            :selected-item-index="selectedRoleIndex"
                            :parent-with-grant="false"
                            :disable-actions="!allowEdit"
                            no-data-text="Please add at least one role!"
                            @on-row-action-click="onDeleteRoleRow"
                            @on-table-row-select="onRoleSelectChange"
                            @on-row-with-grant-change="onRoleWithGrantChange"
                        ></scope-options-table>
                    </v-col>
                </v-row>
                <v-row v-if="allowAdd">
                    <v-col class="pb-0 pt-1">
                        <v-sheet>
                            <v-select
                                v-model="selectedAssignableRole"
                                :items="filteredAssignableRoles"
                                label="Add a role"
                                solo
                                hide-details
                                clearable
                                class="add-scope-options-select"
                                dense
                                eager
                                :suffix="`(${filteredAssignableRoles.length})`"
                                :disabled="
                                    !allowEdit ||
                                        !filteredAssignableRoles.length
                                "
                                @change="onAssignableRolesChange"
                            ></v-select>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script>
import ScopeOptionsTable from './ScopeOptionsTable';
import userScopeMixin from '@/mixins/userScopeMixin';

export default {
    name: 'SingleScopeView',
    components: {
        'scope-options-table': ScopeOptionsTable
    },
    mixins: [userScopeMixin],
    props: {
        allowAdd: {
            type: Boolean,
            required: false,
            default: false
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: true
        },
        // the assigned scopes for the selected user
        scopesData: {
            type: Array,
            required: true
        },
        // scopes for the logged user
        grantableScopesData: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            roleHeaders: [
                { text: 'Role', type: 'text', value: 'role' },
                { text: 'Grant', value: 'withGrant' },
                { text: 'Info', value: 'info' },
                { text: ' ', value: 'action' }
            ],
            selectedRoleIndex: -1,
            // this view has assigned scopes/roles listed in the data table and
            // assignable scopes/roles which is displayed in the add new dropdown
            assignedRoles: [],
            selectedAssignableRole: null,
            assignableRoles: []
        };
    },
    computed: {
        filteredAssignableRoles() {
            // need to filter out the assigned scopes
            return this.assignableRoles
                .filter(item => {
                    return !this.assignedRoles.find(
                        role => role.name === item.name
                    );
                })
                .map(role => {
                    return {
                        text: role.name,
                        value: role
                    };
                });
        },
        isRolesValid() {
            if (this.assignedRoles && this.assignedRoles.length) {
                return (
                    this.assignedRoles.filter(item => {
                        return !!item.error;
                    }).length <= 0
                );
            } else {
                return false;
            }
        }
    },
    watch: {
        scopesData(value) {
            // need to repopulate the screens if the scopeData has changed
            if (value) {
                this.populateData();
            }
        }
    },
    created() {
        this.populateData();
    },
    methods: {
        // ========================= Events ================================
        onDeleteRoleRow(role) {
            this.assignedRoles = this.assignedRoles.filter(
                item => item.roleId !== role.roleId
            );
            this.notifyScopesChanged();
            this.selectedRoleIndex = 0;
        },
        onRoleSelectChange(index) {
            this.selectedRoleIndex = index;
        },
        onRoleWithGrantChange(value) {
            // grant/ungrant role
            if (
                this.assignedRoles &&
                this.assignedRoles.length &&
                this.selectedRoleIndex >= 0
            ) {
                this.assignedRoles[this.selectedRoleIndex].withGrant = value;
                this.notifyScopesChanged();
            }
        },
        onAssignableRolesChange(value) {
            if (value) {
                this.assignedRoles.push({
                    id: -1,
                    roleId: value.id,
                    name: value.name,
                    withGrant: false,
                    info: null,
                    error: null,
                    disableEdit: !this.canEditRole(
                        value,
                        this.scopesData[0],
                        this.grantableScopesData
                    )
                });
                this.selectedRoleIndex = this.assignedRoles.length - 1;
                this.notifyScopesChanged();
            }
            this.selectedAssignableRole = {};
        },
        // ===================== Other =============================
        populateData() {
            this.populateAssignedRoles();
            this.populateAssignableRoles(this.scopesData[0]);
            if (this.assignedRoles && this.assignedRoles.length) {
                this.selectedRoleIndex = 0;
            }
            this.$nextTick(() => {
                this.notifyScopesChanged(false);
            });
        },
        populateAssignedRoles() {
            if (this.scopesData && this.scopesData.length) {
                this.assignedRoles = this.scopesData[0].roles.map(role => {
                    return { ...role };
                });
            }
        },
        populateAssignableRoles(selectedScope) {
            // will get the assignable roles based on the selected scope
            // need to get all roles from selected scope and check if withGrant
            let roles = this.getGrantableRoles(
                selectedScope,
                this.grantableScopesData
            );
            this.assignableRoles = roles;
        },
        notifyScopesChanged(isDirty = true) {
            this.$emit('change', {
                scopes: [{ ...this.scopesData[0], roles: this.assignedRoles }],
                isValid: this.isRolesValid,
                isDirty
            });
        }
    }
};
</script>

<style></style>
